import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../hooks/useAccount'
import { Modal } from '../shared/Modal'
// Reuse the shared list/CTA styling (.reconnect-account-list, .button,
// .button-primary) that lives alongside QuotaExceededModal. The shared Modal
// owns the overlay/header/close affordance, so we only need the body styles.
import '../Subscription/QuotaExceededModal.css'

/**
 * Global modal prompting the user to re-authenticate Instagram accounts whose
 * publishing connection has expired (backend sets `needs_reconnect: true` on the
 * account after auto-retry of transient publish failures is exhausted).
 *
 * Reads `accountsNeedingReconnect` from AccountContext as durable state and
 * shows itself as soon as it is mounted AND the list is non-empty. Mounted
 * globally in App.jsx next to <QuotaExceededModal /> — i.e. after the App-level
 * loading spinner has unmounted — so the signal cannot be missed.
 *
 * Built on shared/Modal.jsx for accessibility (portal, Escape, focus trap,
 * scroll lock). CTA navigates to /accounts where the existing OAuth reconnect
 * flow lives; the Modal's own close button only dismisses (no navigation).
 */
const InstagramReconnectModal = () => {
  const { t } = useTranslation('errors')
  const navigate = useNavigate()
  const { accountsNeedingReconnect } = useAccount()
  const [dismissed, setDismissed] = useState(false)

  const affectedAccounts = Array.isArray(accountsNeedingReconnect)
    ? accountsNeedingReconnect
    : []

  const open = affectedAccounts.length > 0 && !dismissed

  const handleReconnect = () => {
    setDismissed(true)
    navigate('/accounts')
  }

  const handleDismiss = () => {
    setDismissed(true)
  }

  const accountNames = affectedAccounts
    .map(
      (account) =>
        account.instagram_username || account.username || account.name || account.id
    )
    .filter(Boolean)

  return (
    <Modal
      isOpen={open}
      onClose={handleDismiss}
      title={t('reconnect.title')}
      size="md"
    >
      <p className="quota-message">{t('reconnect.message')}</p>

      <ul className="reconnect-account-list">
        {accountNames.map((name, index) => (
          <li key={`${name}-${index}`}>@{name}</li>
        ))}
      </ul>

      <div className="modal-footer">
        <button className="button button-primary" onClick={handleReconnect}>
          {t('reconnect.button')}
        </button>
      </div>
    </Modal>
  )
}

export default InstagramReconnectModal
